import { Avatar, Box, Chip, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import { OLButton } from "OLButton";
import { OLPaper } from "OLPaper";
import { hasAnyAccessToResource } from "api/general";
import { useReward } from "api/rewards";
import { useAppSelector } from "app/hooks";
import { UnlimitedValueWrapper } from "components/FormControls/UnlimitedValueWrapper";
import { Subheader } from "components/Header/Subheader/Subheader";
import { SubheaderMenuButton } from "components/Header/Subheader/SubheaderMenuButton";
import { SubheaderWrapper } from "components/Header/Subheader/SubheaderWrapper";
import { Breadcrumbs, TopHeader } from "components/Header/TopHeader/TopHeader";
import { ImportCsvModal } from "components/Layout/ImportCsv/ImportCsvModal/ImportCsvModal";
import AddCoupon from "components/Tooltips/AddCoupon";
import AvailableCouponsList from "components/Tooltips/AvailableCouponsList";
import ClaimRewardModal from "components/Tooltips/ClaimRewardModal";
import { Tooltip } from "components/Tooltips/Tooltip";
import { useMessage } from "hooks/intl";
import { useIsOverflown } from "hooks/useIsOverflown";
import { kebabCase } from "lodash";
import { useState } from "react";
import ReactHtmlParser from "react-html-parser";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useNavigate, useParams } from "react-router-dom";
import { selectCurrentStoreData } from "reduxState/currentStoreData/currentStoreDataSlice";
import { selectDefaultWalletData } from "reduxState/defaultWalletData/defaultWalletDataSlice";
import { Nullable } from "types/global";
import { alphabetizeByProperty } from "utilities/arrays";
import { OLDate } from "utilities/dateFormat";
import { links } from "utilities/links";
import { formatCurrencyValue, formatNumberValue } from "utilities/numbers";
import { isEmpty } from "utilities/objects";
import { getAcronym, pickPluralizedUnit } from "utilities/strings";
import { useStyles } from "./SingleReward.styles";
import { SingleRewardFulfillmentsList } from "./SingleRewardFulfillmentsList";

enum Modals {
  AddCoupon = "addCoupon",
  ClaimReward = "claimReward",
  ImportCoupons = "importCoupons",
}

interface DateInterface {
  allTime?: boolean | string;
  from?: Nullable<string>;
  to?: Nullable<string>;
}

export function SingleReward() {
  const classes = useStyles();
  const { message } = useMessage();
  const navigate = useNavigate();
  const { id: rewardId } = useParams<{ id: string }>();
  const { currency } = useAppSelector(selectCurrentStoreData);
  const { observeElement, isOverflown } = useIsOverflown();

  const { useRewardData, importCoupons } = useReward();
  const { data: rewardData } = useRewardData(rewardId as string);
  const [openedModalType, setOpenedModalType] = useState<Modals | null>(null);
  const closeModal = () => setOpenedModalType(null);

  const defaultWallet = useAppSelector(selectDefaultWalletData);

  const allTime = (data?: DateInterface) => data?.allTime;

  const periodical = (data?: DateInterface) =>
    !allTime(data) && data?.from && data?.to;

  const createLabel = ({
    data,
    allTimeString,
  }: {
    data?: DateInterface;
    allTimeString: string;
  }) =>
    (allTime(data) && message({ id: allTimeString })) ||
    (periodical(data) && `${OLDate(data?.from!)} - ${OLDate(data?.to!)}`) ||
    undefined;

  const details = [
    {
      label: "status",
      value: message({
        id: `${rewardData?.active ? "active" : "inactive"}`,
      }),
    },
    {
      label: "description",
      value: rewardData?.shortDescription || "-",
    },
    {
      label: "priceInPoints",
      value: rewardData?.costInPoints
        ? `${formatNumberValue(rewardData?.costInPoints)} ${pickPluralizedUnit(
            rewardData?.costInPoints,
            defaultWallet?.unitSingularName,
            defaultWallet?.unitPluralName,
          )}`
        : "-",
    },
    {
      label: "couponValue",
      value: formatCurrencyValue(rewardData?.couponValue) || "0",
    },
    {
      label: "costWithCurrency",
      value: formatCurrencyValue(rewardData?.price),
    },
    {
      label: "visibility",
      value: createLabel({
        data: rewardData?.visibility,
        allTimeString: "isAllTimeActive",
      }),
    },
    {
      label: "availability",
      value: createLabel({
        data: rewardData?.activity,
        allTimeString: "isAllTimeActive",
      }),
    },
    {
      label: "createdOn",
      value: OLDate(rewardData?.createdAt),
    },
    {
      label: "rewardsLimit",
      value: <UnlimitedValueWrapper value={rewardData?.usageLimit?.general} />,
    },
    {
      label: "limitPerMember",
      value: <UnlimitedValueWrapper value={rewardData?.usageLimit?.perUser} />,
    },
  ];

  const chips = [
    {
      label: "categories",
      values: rewardData?.categoryNames
        ? Object.values(rewardData.categoryNames)
        : [],
    },
    {
      label: "customAttributes",
      values: rewardData?.labels
        ? rewardData.labels.map(
            (item: { key: string; value: string }) =>
              `${item.key}: ${item.value}`,
          )
        : [],
    },
    {
      label: "tiersLimit",
      values: rewardData?.levelNames
        ? Object.values(rewardData.levelNames)
        : [],
    },
    {
      label: "segmentsLimit",
      values: rewardData?.segmentNames
        ? Object.values(rewardData.segmentNames)
        : [],
    },
  ];
  const showCoupons =
    (rewardData?.reward === "static_coupon" ||
      rewardData?.reward === "dynamic_coupon" ||
      rewardData?.reward === "conversion_coupon") &&
    isEmpty(rewardData?.couponGenerator);

  const hasAnyLanguagePermission = hasAnyAccessToResource("LANGUAGE");

  const dataTestId = "single-reward";

  const breadcrumbs: Breadcrumbs = {
    parent: {
      label: message({ id: "rewards" }),
      path: links.rewardsList,
    },
  };

  return (
    <>
      <TopHeader
        title={rewardData ? rewardData?.name : ""}
        clearLink={links.rewardsList}
        breadcrumbs={breadcrumbs}
      />
      <Subheader>
        <SubheaderWrapper alignRight>
          <SubheaderMenuButton
            text={message({ id: "actions" })}
            menuItems={alphabetizeByProperty(
              [
                ...(hasAnyLanguagePermission
                  ? [
                      {
                        onClick: () => navigate(links.editReward(rewardId)),
                        text: message({ id: "edit" }),
                      },
                    ]
                  : []),
                {
                  text: message({ id: "claimFor" }),
                  onClick: () => {
                    setOpenedModalType(Modals.ClaimReward);
                  },
                },
                ...(showCoupons
                  ? [
                      {
                        text: message({ id: "addCoupon" }),
                        onClick: () => {
                          setOpenedModalType(Modals.AddCoupon);
                        },
                      },
                      {
                        text: message({ id: "importCoupons" }),
                        onClick: () => {
                          setOpenedModalType(Modals.ImportCoupons);
                        },
                      },
                    ]
                  : []),
              ],
              "text",
            )}
          />
        </SubheaderWrapper>
      </Subheader>
      <Grid className={classes.gridContainer} container spacing={10}>
        <Grid className={classes.gridChild} item xs={3}>
          <OLPaper className={classes.detailsPaper}>
            <Box className={classes.mainDataContainer}>
              {rewardData ? (
                <Avatar
                  alt={rewardData.name}
                  className={classes.avatar}
                  variant="square"
                >
                  {getAcronym(rewardData.name)}
                </Avatar>
              ) : (
                <Skeleton height={48} width={180} />
              )}
              <Box className={classes.overflowText} ref={observeElement}>
                <Tooltip
                  text={rewardData?.name}
                  disableHoverListener={!isOverflown}
                  placement="bottom-start"
                >
                  <span className={classes.largeText}>
                    {rewardData ? rewardData.name : <Skeleton width={100} />}
                  </span>
                </Tooltip>
                <Typography className={classes.smallText}>
                  {rewardData ? (
                    `${message({
                      id:
                        rewardData?.couponValueType === "percentage"
                          ? "percentageCoupon"
                          : rewardData?.reward,
                    })} ${message({ id: "reward" }).toLowerCase()}`
                  ) : (
                    <Skeleton width={100} />
                  )}
                </Typography>
              </Box>
            </Box>
            <Box className={classes.rewardDataContainer}>
              {details.map((item, index: number) => (
                <Box key={index}>
                  <Typography
                    className={classes.smallText}
                    data-test-id={`${dataTestId}-${kebabCase(item.label)}-key`}
                  >
                    {message({ id: item.label }, { currency })}
                  </Typography>
                  <Typography
                    className={`${classes.largeText} ${classes.htmlContainer}`}
                    data-test-id={`${dataTestId}-${kebabCase(
                      item.label,
                    )}-value`}
                  >
                    {rewardData ? (
                      typeof item.value === "string" ? (
                        (ReactHtmlParser(item.value || "-") as React.ReactNode)
                      ) : (
                        item.value
                      )
                    ) : (
                      <Skeleton width={100} />
                    )}
                  </Typography>
                </Box>
              ))}
              {chips.map(
                (chip: { label: string; values: any[] }, index: number) => (
                  <Box key={index}>
                    <Typography
                      className={classes.smallText}
                      data-test-id={`${dataTestId}-${kebabCase(
                        chip.label,
                      )}-chip-label`}
                    >
                      {message({ id: chip.label })}
                    </Typography>
                    {rewardData ? (
                      <>
                        {chip.values.length
                          ? chip.values?.map(
                              (chipValue: string, itemIndex: number) => (
                                <Chip
                                  className={classes.singleFilter}
                                  key={itemIndex}
                                  label={chipValue}
                                  data-test-id={`${dataTestId}-${kebabCase(
                                    chipValue,
                                  )}-chip-value`}
                                />
                              ),
                            )
                          : "-"}
                      </>
                    ) : (
                      <Skeleton width={100} />
                    )}
                  </Box>
                ),
              )}
              <OLButton
                data-test-id={`${dataTestId}-see-all-reward-details-button`}
                className={classes.btn}
                color="secondary"
                disabled={!hasAnyLanguagePermission}
                onClick={() => navigate(links.editReward(rewardId))}
              >
                {message({ id: "rewardDetails" })}
              </OLButton>
            </Box>
          </OLPaper>
        </Grid>
        <Grid className={classes.gridContainer} item xs={9}>
          {showCoupons && (
            <Grid className={classes.gridContainer} item xs={12}>
              <AvailableCouponsList rewardId={rewardId as string} />
            </Grid>
          )}
          <Grid className={classes.gridContainer} item xs={12}>
            <SingleRewardFulfillmentsList rewardId={rewardId as string} />
          </Grid>
        </Grid>
      </Grid>
      <ClaimRewardModal
        open={openedModalType === Modals.ClaimReward}
        closeModal={closeModal}
        reward={{ id: rewardId as string, type: rewardData?.reward as string }}
      />
      {openedModalType && openedModalType === "addCoupon" && (
        <AddCoupon
          closeModal={closeModal}
          open={openedModalType === Modals.AddCoupon}
          rewardCode={rewardId as string}
        />
      )}
      <ImportCsvModal
        headerMessage="importCoupons"
        closeModal={closeModal}
        isModalOpen={openedModalType === Modals.ImportCoupons}
        importData={importCoupons}
      />
    </>
  );
}
